import React, { useContext, useEffect, useRef, useState } from "react";

import { MaterialReactTable } from "material-react-table";
import { Plane, Eye } from "lucide-react";
import { base_url } from "../../config";
import axios from "axios";
import VisaModal from "./VisaModal";
import RegistrationContext from "../../contexts/RegistrationContext";
import {
  darkBodyCellSx,
  darkHeadCellSx,
  darkPaperSx,
  darkRowSx,
  darkSearchSx,
  darkToolbarSx,
} from "../Codes/darkTable";

const VisaTable = () => {
  const [data, setData] = useState([]);
  const [code, setCode] = useState("");
  const [type, setType] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [rowData, setRowData] = useState(null);
  const skippedRef = useRef(new Set());
  const { reloadAdminBadges, setReloadAdminBadges } = useContext(RegistrationContext);

  const visaKey = (r) => `${r.code}::${r.type}`;

  const fetchVisa = async () => {
    try {
      const response = await axios.get(`${base_url}/api/admin/visa`);
      return response.data || [];
    } catch (error) {
      console.error("Error visa applications:", error);
      return [];
    }
  };

  const reloadData = async () => {
    const rows = await fetchVisa();
    setData(rows);
    return rows;
  };

  useEffect(() => {
    reloadData();
  }, [reloadAdminBadges]);

  const openRow = (row) => {
    setRowData(row);
    setCode(row.code);
    setType(row.type);
    setModalOpen(true);
  };

  const handleResolved = async () => {
    setReloadAdminBadges(reloadAdminBadges + 1);
    const rows = await reloadData();
    const justHandled = rowData ? visaKey(rowData) : null;
    const next = rows.find(
      (r) => visaKey(r) !== justHandled && !skippedRef.current.has(visaKey(r))
    );
    if (next) {
      openRow(next);
    } else {
      setModalOpen(false);
      setRowData(null);
      setCode("");
      setType("");
    }
  };

  const handleSkip = async () => {
    if (rowData) skippedRef.current.add(visaKey(rowData));
    const rows = await fetchVisa();
    const next = rows.find(
      (r) => !skippedRef.current.has(visaKey(r))
    );
    if (next) {
      openRow(next);
    } else {
      setModalOpen(false);
      setRowData(null);
      setCode("");
      setType("");
    }
  };

  const columns = [
    {
      accessorKey: "code",
      header: "Code",
      size: 100,
      Cell: ({ cell }) => (
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
          {cell.getValue()}
        </span>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      Cell: ({ cell }) => <span className="text-slate-100">{cell.getValue()}</span>,
    },
    {
      accessorKey: "email",
      header: "Email",
      Cell: ({ cell }) => (
        <span className="text-slate-400 text-xs">{cell.getValue()}</span>
      ),
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 120,
      Cell: ({ cell }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium bg-sky-500/15 text-sky-300 border-sky-500/30">
          {cell.getValue() || "—"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableColumnActions: false,
      size: 110,
      Cell: ({ row }) => (
        <div className="flex justify-end">
          <button
            onClick={() => openRow(row.original)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
            Review
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] px-4 py-5 space-y-4">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Plane className="h-5 w-5 text-blue-400" />
            Visa validations
            <span className="text-xs font-normal text-slate-400">
              ({data.length} pending)
            </span>
          </h1>
        </header>

        <div className="rounded-2xl overflow-hidden">
          <MaterialReactTable
            columns={columns}
            data={data}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            initialState={{ density: "compact", pagination: { pageSize: 25, pageIndex: 0 } }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
          />
        </div>

        <VisaModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          row={rowData}
          code={code}
          type={type}
          onAccept={handleResolved}
          onDecline={handleResolved}
          onSkip={handleSkip}
        />
      </div>
    </div>
  );
};

export default VisaTable;
